import Link from "next/link";

import type { PricingTable } from "@/content/comparisons/schema";

interface PricingComparisonProps {
  heading: string;
  rebolt: PricingTable;
  competitor: PricingTable;
  winnerNote: string;
}

function highlightRebolt(text: string): React.ReactNode {
  const idx = text.indexOf("Rebolt");
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-rebolt-500">Rebolt</span>
      {text.slice(idx + 6)}
    </>
  );
}

function PricingCard({ table, isRebolt }: { table: PricingTable; isRebolt: boolean }) {
  const columns = table.columns ?? [];
  const rows = table.rows ?? [];
  const hasTable = columns.length > 0 && rows.length > 0;

  return (
    <div className="rounded-2xl border border-ash-200 bg-white p-6 md:p-8">
      <h3 className="mb-6 font-clash text-xl font-medium tracking-[-0.01em] text-ash-900 md:text-2xl">
        {isRebolt ? (
          <>
            <span className="text-rebolt-500">Rebolt </span>Pricing
          </>
        ) : (
          table.heading
        )}
      </h3>

      {hasTable && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-ash-200">
                {columns.map((col) => (
                  <th key={col} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-ash-500">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-ash-100 last:border-b-0">
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={`px-4 py-3.5 text-sm ${j === 0 ? "font-medium text-ash-900" : "text-ash-600"}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {table.description && <p className="text-base leading-relaxed text-ash-600">{table.description}</p>}

      {table.note && (
        <div className="mt-5 rounded-lg border border-ash-200 bg-ash-100/50 px-4 py-3">
          <p className="text-sm text-ash-600">{table.note}</p>
        </div>
      )}

      {isRebolt && (
        <div className="mt-6 text-center">
          <Link
            href="https://cal.com/team/builtright/launch"
            className="inline-flex items-center rounded-full bg-rebolt-500 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-rebolt-600"
          >
            Try Rebolt Now
          </Link>
        </div>
      )}
    </div>
  );
}

export function PricingComparison({ heading, rebolt, competitor, winnerNote }: PricingComparisonProps) {
  return (
    <section className="bg-ash-100 py-16 md:py-24">
      <div className="mx-auto max-w-5xl px-6">
        <h2 className="mb-10 text-center font-clash text-3xl font-medium leading-tight tracking-snug text-ash-900 md:text-5xl">
          {heading}
        </h2>

        <div className="space-y-8">
          <PricingCard table={rebolt} isRebolt />
          <PricingCard table={competitor} isRebolt={false} />
        </div>

        <div className="mt-8 rounded-xl border border-rebolt-200 bg-rebolt-100/50 px-5 py-4">
          <p className="text-base text-ash-700">{highlightRebolt(winnerNote)}</p>
        </div>
      </div>
    </section>
  );
}
